export const SESSION_KEY = "staycasa_session";

export type PresenceEntry = {
  staffId: number;
  online: boolean;
  lastSeen: string | null;
  loginAt: string | null;
};

export type Session = {
  staffId: number;
  email: string;
  role: string;
  name: string;
};

export function getPresence(): PresenceEntry[] { return []; }

export async function getServerSession(): Promise<Session | null> {
  try {
    const { headers, cookies } = await import("next/headers");
    // Ace Intel bearer token auth
    const headerList = await headers();
    if (headerList.get("x-ace-authenticated") === "1") {
      return { staffId: 1, email: "ace@staycasa.co.uk", role: "Admin", name: "Ace Intel" };
    }
    const { verifySession } = await import("./session");
    const jar = await cookies();
    const token = jar.get("staycasa_auth")?.value;
    if (!token) return null;
    // Verify JWT and extract session payload
    const session = verifySession(token);
    if (!session || typeof session.staffId !== "number") return null;
    return {
      staffId: session.staffId,
      email: session.email ?? "",
      role: session.role ?? "",
      name: session.name ?? "",
    };
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  if (typeof window === "undefined") return null;
  try {
    const saved = localStorage.getItem(SESSION_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export function setSession(session: Session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

export type ChangelogEntry = {
  id: string;
  timestamp: string;
  actorId: number;
  actorName: string;
  targetId: number;
  targetName: string;
  action: string;
  diff: Record<string, unknown>;
  snapshot: unknown;
};

export type ShiftPattern = {
  staffId: number;
  days: number[];
  startTime: string;
  endTime: string;
  location: string;
  notes: string;
  enabled: boolean;
};

export type ClockEntry = {
  id: string;
  staffId: number;
  clockIn: string;
  clockOut: string | null;
  clockInLat?: number | null;
  clockInLng?: number | null;
  clockOutLat?: number | null;
  clockOutLng?: number | null;
};

export function generatePassword(length = 12): string {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*";
  const all = upper + lower + digits + symbols;
  const rand = (str: string) => str[Math.floor(Math.random() * str.length)];
  const required = [rand(upper), rand(lower), rand(digits), rand(symbols)];
  const rest = Array.from({ length: length - 4 }, () => rand(all));
  return [...required, ...rest].sort(() => Math.random() - 0.5).join("");
}
